import CryptoJS from "crypto-js";
import {
  PDFArray,
  PDFDict,
  PDFDocument,
  PDFHexString,
  PDFRawStream,
  PDFStream,
  PDFString,
} from "pdf-lib";

// A redacted PDF has no text layer, but Chrome, Edge and Acrobat run OCR on
// image-only pages and then let the visible text be selected and copied.
// To stop that, the file is encrypted with an empty user password (so it
// still opens without a prompt) and a random owner password that is thrown
// away, with copying and text extraction switched off. Viewers honour these
// permissions. Standard security handler, RC4 128-bit (V2 / R3).

const PASSWORD_PAD = new Uint8Array([
  0x28, 0xbf, 0x4e, 0x5e, 0x4e, 0x75, 0x8a, 0x41, 0x64, 0x00, 0x4e, 0x56,
  0xff, 0xfa, 0x01, 0x08, 0x2e, 0x2e, 0x00, 0xb6, 0xd0, 0x68, 0x3e, 0x80,
  0x2f, 0x0c, 0xa9, 0xfe, 0x64, 0x53, 0x69, 0x7a,
]);

// Printing allowed (normal and high quality). Copying, text extraction
// (including for accessibility tools), editing, annotating, form filling and
// page assembly are not.
const PERMISSIONS = -1852; // 0xFFFFF8C4

const concat = (...parts) => {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  parts.forEach((p) => {
    out.set(p, at);
    at += p.length;
  });
  return out;
};

const md5 = (...parts) => {
  const { words } = CryptoJS.MD5(CryptoJS.lib.WordArray.create(concat(...parts)));
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) out[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  return out;
};

function rc4(key, data) {
  const s = new Uint8Array(256);
  for (let i = 0; i < 256; i++) s[i] = i;
  for (let i = 0, j = 0; i < 256; i++) {
    j = (j + s[i] + key[i % key.length]) & 0xff;
    [s[i], s[j]] = [s[j], s[i]];
  }
  const out = new Uint8Array(data.length);
  for (let n = 0, i = 0, j = 0; n < data.length; n++) {
    i = (i + 1) & 0xff;
    j = (j + s[i]) & 0xff;
    [s[i], s[j]] = [s[j], s[i]];
    out[n] = data[n] ^ s[(s[i] + s[j]) & 0xff];
  }
  return out;
}

// R3: the RC4 step is repeated 19 more times with the key XORed by 1..19.
const rc4Rounds = (key, data) => {
  let out = rc4(key, data);
  for (let i = 1; i <= 19; i++) out = rc4(key.map((b) => b ^ i), out);
  return out;
};

const md5Rounds = (hash) => {
  for (let i = 0; i < 50; i++) hash = md5(hash);
  return hash;
};

const le32 = (n) =>
  new Uint8Array([n & 0xff, (n >> 8) & 0xff, (n >> 16) & 0xff, (n >>> 24) & 0xff]);

const toHex = (bytes) =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

const randomBytes = (n) => crypto.getRandomValues(new Uint8Array(n));

function encryptValue(obj, key) {
  if (obj instanceof PDFString || obj instanceof PDFHexString) {
    return PDFHexString.of(toHex(rc4(key, obj.asBytes())));
  }
  if (obj instanceof PDFArray) {
    for (let i = 0; i < obj.size(); i++) obj.set(i, encryptValue(obj.get(i), key));
  } else if (obj instanceof PDFDict) {
    obj.entries().forEach(([name, value]) => obj.set(name, encryptValue(value, key)));
  }
  return obj;
}

/**
 * Encrypts a pdf-lib document in place so it opens freely but can't be
 * copied from. Save it afterwards with `useObjectStreams: false`.
 */
export function applyCopyProtection(doc) {
  const { context } = doc;
  const fileId = randomBytes(16);

  const ownerKey = md5Rounds(md5(randomBytes(32)));
  const O = rc4Rounds(ownerKey, PASSWORD_PAD);
  const fileKey = md5Rounds(md5(PASSWORD_PAD, O, le32(PERMISSIONS), fileId));
  const U = concat(rc4Rounds(fileKey, md5(PASSWORD_PAD, fileId)), new Uint8Array(16));

  context.enumerateIndirectObjects().forEach(([ref, obj]) => {
    const num = ref.objectNumber;
    const gen = ref.generationNumber;
    const key = md5(
      fileKey,
      new Uint8Array([num & 0xff, (num >> 8) & 0xff, (num >> 16) & 0xff, gen & 0xff, (gen >> 8) & 0xff]),
    );
    if (obj instanceof PDFStream) {
      encryptValue(obj.dict, key);
      context.assign(ref, PDFRawStream.of(obj.dict, rc4(key, obj.getContents())));
    } else {
      encryptValue(obj, key);
    }
  });

  context.trailerInfo.Encrypt = context.obj({
    Filter: "Standard",
    V: 2,
    R: 3,
    Length: 128,
    P: PERMISSIONS,
    O: PDFHexString.of(toHex(O)),
    U: PDFHexString.of(toHex(U)),
  });
  const id = PDFHexString.of(toHex(fileId));
  context.trailerInfo.ID = context.obj([id, id]);
}

// Same as applyCopyProtection, for a PDF that is already built (e.g. by jsPDF).
export async function copyProtectPdfBytes(bytes) {
  const doc = await PDFDocument.load(bytes, { updateMetadata: false });
  applyCopyProtection(doc);
  return doc.save({ useObjectStreams: false });
}
